import React, { useEffect, useState } from 'react';
import { formatDateTime } from 'utils/date.util';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Button,
  Flex,
  Badge,
  SimpleGrid,
} from '@chakra-ui/react';

import { useColors } from './colors';
import {
  getShipStationTaggerScheduleApi,
  updateShipStationTaggerScheduleApi,
} from 'features/third-party-integrations/third-party-integrations.api';

const ShipStationTaggerSchedule = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const { shipStationTaggerSchedule, updateShipStationTaggerScheduleStatus } = useSelector(
    (state) => state.thirdPartyIntegrations,
  );
  const { textColor, borderColor, inputBgColor, inputColor } = useColors();

  const [runsPerDay, setRunsPerDay] = useState(1);
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    dispatch(getShipStationTaggerScheduleApi());
  }, [dispatch]);

  useEffect(() => {
    if (shipStationTaggerSchedule) {
      setRunsPerDay(shipStationTaggerSchedule.runsPerDay ?? 1);
      setIsEnabled(Boolean(shipStationTaggerSchedule.isScheduleEnabled));
    }
  }, [shipStationTaggerSchedule]);

  const handleSave = () => {
    dispatch(
      updateShipStationTaggerScheduleApi({
        runsPerDay: Number(runsPerDay),
        isScheduleEnabled: isEnabled,
      }),
    );
  };

  const scheduleDescription =
    shipStationTaggerSchedule?.scheduleDescription ||
    'Automatically tag ShipStation orders with supplier info.';

  const content = (
    <VStack align="stretch" spacing={5}>
      {embedded && (
        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme={isEnabled ? 'green' : 'gray'} variant="subtle">
            {isEnabled ? 'Schedule on' : 'Schedule off'}
          </Badge>
          <Text fontSize="sm" color="gray.500">
            {scheduleDescription}
          </Text>
        </HStack>
      )}

      <FormControl>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0} color={textColor} fontSize="sm">
            Enable scheduled runs
          </FormLabel>
          <Switch
            isChecked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
            colorScheme="blue"
          />
        </Flex>
      </FormControl>

      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
        <FormControl>
          <FormLabel color={textColor} fontSize="sm">
            Runs per day (1–24)
          </FormLabel>
          <Select
            value={runsPerDay}
            onChange={(e) => setRunsPerDay(e.target.value)}
            bg={inputBgColor}
            color={inputColor}
            isDisabled={!isEnabled}
          >
            {Array.from({ length: 24 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </FormControl>

        {shipStationTaggerSchedule?.lastRunAt && (
          <FormControl>
            <FormLabel color={textColor} fontSize="sm">
              Last run
            </FormLabel>
            <Text fontSize="sm" color="gray.500" pt={2}>
              {formatDateTime(shipStationTaggerSchedule.lastRunAt)}
            </Text>
          </FormControl>
        )}
      </SimpleGrid>

      <HStack>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={updateShipStationTaggerScheduleStatus === 'pending'}
        >
          Save schedule
        </Button>
      </HStack>
    </VStack>
  );

  if (embedded) {
    return content;
  }

  return (
    <Box
      mt={6}
      p={5}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
    >
      <Text fontWeight="semibold" color={textColor} mb={2}>
        Order supplier tagger schedule
      </Text>
      <Text fontSize="sm" color="gray.500" mb={4}>
        {scheduleDescription}
      </Text>
      {content}
    </Box>
  );
};

export default ShipStationTaggerSchedule;
